import * as React from 'react'
import { Provider as ReduxProvider } from 'react-redux'
import {
  getWidgetProps,
  WPSClient,
  Args,
  withArgs,
  Widget,
} from '@utils'
import { requestServiceData } from '../../utils/requestServiceData'
import { DVUI } from '@amzn/dvui/dist/DVUI'
import { configureStore } from '../../store'
import { IDetailPageState } from '../../reducers'
import { Collections } from '../../components/collection/collections'
import {isLiveEventTitleSelector, isStarlightEnhancedSelector} from '../../selectors/shared'
import {
  normalizeGetDetailPage,
  normalizeGetPreferences,
  normalizeStringMap,
  NormalizeGetDetailPageType,
  NormalizeGetPreferencesType,
  NormalizeStringMapType,
} from '../../data/normalize'
import { getBtfInput } from '../../data/getDetailPageInputs'
import { isTreatment, safeBoolean } from '../../utils/utils'
import { ErrorBoundary } from '../../components/error/ErrorBoundary'
import { blockedPageSelector } from '../../selectors/component/pageBlockComponentSelectors'
import { DPPlaybackArea } from '../../utils/DPPlaybackArea'
import { BtfTabs } from '../../components/tabs/btfTabs'
import { StringBundle } from '../../components/localizer/stringBundle'
import { DpWidgetContext } from '../../utils/dpWidgetContext'
import { buildGetString } from '../../utils/stringUtils'
import { Faq } from '../../components/faq/faq'
import { ContactUs } from '../../components/contactUs/contactUs'
import { BtfProductMetaInfo } from '../../components/productMetaInfo/btfProductMetaInfo'
import { Carousels } from '../../components/carousel/carousel'
import { CastAndCrew as TabbedCastAndCrew } from '../../components/castAndCrew/castAndCrewTabs'
import { ScoutBanner } from '../../components/scoutBanner/scoutBanner'
import { CastAndCrew } from '../../components/castAndCrew/castAndCrew'
import { OtherFormats } from '../../components/otherFormats/otherFormats'
import { isInternetExplorerSelector, pageTypeSelector, subPageTypeSelector } from '../../selectors/pageContext'
import '../../../../shared/global.module.scss';

export type BtfProps =
  | {
      state: NormalizeGetDetailPageType &
        NormalizeGetPreferencesType &
        Pick<IDetailPageState, 'features'>
    } & NormalizeStringMapType
  | {
      state: Pick<IDetailPageState, 'features' | 'pageTitleId'>
    }

export const getProps: getWidgetProps<BtfProps> = async (
  {
    context,
    titleID,
    isElcano,
    enableLeavingSoonBtf,
    useFaq,
    useContactUs,
    useTabbedCastAndCrew,
    logger,
    btfTabTreatment,
    enableStarlightCache,
    enableSPAGiftCard
  },
  api
) => {
  const features = {
    useFaq: safeBoolean(useFaq),
    useContactUs: safeBoolean(useContactUs),
    useTabbedCastAndCrew: safeBoolean(useTabbedCastAndCrew),
    btfTabTreatment,
    enableLeavingSoonBtf: safeBoolean(enableLeavingSoonBtf),
    enableSPAGiftCard: safeBoolean(enableSPAGiftCard)
  }

  const operation = (wpsClient: WPSClient) =>
    wpsClient.GetDetailPage(getBtfInput({ context, titleID, isElcano: safeBoolean(isElcano)}, features.useFaq, features.useContactUs))

  const reply = await requestServiceData({
    context,
    api,
    operation,
    logger,
    titleID,
    stringBundle: StringBundle.BTF,
    enableStarlightCache: safeBoolean(enableStarlightCache),
    requestPreferences: true,
    requestSPS: true
  })

  return {
    state: {
      features,
      exploreTabContent: reply.spsResponse,
      ...normalizeGetDetailPage(titleID, reply.operationResponse),
      ...normalizeGetPreferences(reply.preferencesResponse!),
    },
    ...normalizeStringMap(reply.stringMap!),
  }
}

const BtfWidget = (props: BtfProps & Args) => {
  const {logger, context: requestContext, state, strings} = props
  const store = configureStore({...state, requestContext}, logger)

  // Page block prevents any render in btf. Message will be shown in atf
  const pageBlock = blockedPageSelector(store.getState())
  if (pageBlock) {
    return null
  }

  const pageType = pageTypeSelector(store.getState())
  const subPageType = subPageTypeSelector(store.getState())
  const isStarlight = isStarlightEnhancedSelector(store.getState())

  const shouldShowTabs = !isLiveEventTitleSelector(store.getState())
    && (isTreatment(state.features.btfTabTreatment, 'T1', 'T2') && !isInternetExplorerSelector(store.getState()) || isStarlight)

  return (
    <DVUI>
      <DpWidgetContext.Provider value={{
        getString: buildGetString(strings, logger, requestContext),
        pageType,
        subPageType
      }}>
        <ReduxProvider store={store}>
          <ErrorBoundary name='btf'>
            <DPPlaybackArea>
              { shouldShowTabs ? <BtfTabs /> :
                  <>
                    <Collections />
                    <Carousels />
                    <TabbedCastAndCrew />
                    <ScoutBanner />
                    <CastAndCrew />
                    <BtfProductMetaInfo />
                    <Faq />
                    <ContactUs />
                    <OtherFormats />
                  </>
                }
            </DPPlaybackArea>
          </ErrorBoundary>
        </ReduxProvider>
      </DpWidgetContext.Provider>
    </DVUI>
  )
}

export const Btf: Widget<BtfProps> = {
  name: 'detail-btf',
  getProps: global.__SERVER__ === true ? getProps : undefined,
  Component: withArgs(BtfWidget) as React.ComponentType<any>,
}
